import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';
import Footer from '../components/Footer';

export default function MyAccountPage() {
  const navigate = useNavigate();
  const { user, token, updateUser, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);

  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    numero_telefono: '',
    data_nascita: '',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [emailData, setEmailData] = useState({
    newEmail: '',
    currentPassword: ''
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
    emailPassword: false
  });

  // Notification preferences state
  const [notificationPrefs, setNotificationPrefs] = useState({
    notify_participation_status: true,
    notify_hike_cancelled: true,
    notify_new_registration: true,
    notify_registration_cancelled: true,
    notify_new_user_registered: true
  });
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [prefsSaving, setPrefsSaving] = useState({});
  const [prefsError, setPrefsError] = useState('');
  const [prefsSaved, setPrefsSaved] = useState({});

  // GDPR: Data export and account deletion
  const [isExportingData, setIsExportingData] = useState(false);
  const [exportError, setExportError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeletePassword, setShowDeletePassword] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        nome: user.nome || '',
        cognome: user.cognome || '',
        email: user.email || '',
        numero_telefono: user.numero_telefono || '',
        data_nascita: user.data_nascita || '',
      });
    }
  }, [user]);

  // Fetch notification preferences
  useEffect(() => {
    const fetchNotificationPrefs = async () => {
      if (!token) return;
      try {
        const response = await fetch(`${API_URL}/users/notification-preferences`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setNotificationPrefs(data);
        }
      } catch (err) {
        console.error('Failed to fetch notification preferences:', err);
      } finally {
        setPrefsLoading(false);
      }
    };
    fetchNotificationPrefs();
  }, [token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCancel = () => {
    // Reset form data to original user data
    setFormData({
      nome: user.nome || '',
      cognome: user.cognome || '',
      email: user.email || '',
      numero_telefono: user.numero_telefono || '',
      data_nascita: user.data_nascita || '',
    });
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_URL}/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          nome: formData.nome,
          cognome: formData.cognome,
          numero_telefono: formData.numero_telefono || null,
data_nascita: formData.data_nascita || null,
         })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Impossibile aggiornare i dati');
      }

      const data = await response.json();

      // Update user data in context
      if (data.user) {
        updateUser(data.user);
      }

      setSuccess('Dati aggiornati con successo!');
      setIsEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');

    // Validate passwords match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('Le nuove password non corrispondono');
      setPasswordLoading(false);
      return;
    }

    // Validate password length
    if (passwordData.newPassword.length < 8) {
      setPasswordError('La nuova password deve contenere almeno 8 caratteri');
      setPasswordLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/users/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Impossibile cambiare la password');
      }

      setPasswordSuccess('Password cambiata con successo!');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      // Close the password change form after 2 seconds
      setTimeout(() => {
        setIsChangingPassword(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (err) {
      setPasswordError(err.message);
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCancelPasswordChange = () => {
    setIsChangingPassword(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setEmailData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setEmailLoading(true);
    setEmailError('');
    setEmailSuccess('');

    try {
      const response = await fetch(`${API_URL}/users/request-email-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          new_email: emailData.newEmail,
          current_password: emailData.currentPassword
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Impossibile cambiare l\'email');
      }

      const data = await response.json();
      setEmailSuccess(data.message);
      setEmailData({
        newEmail: '',
        currentPassword: ''
      });

      // Close the email change form after 3 seconds
      setTimeout(() => {
        setIsChangingEmail(false);
        setEmailSuccess('');
      }, 3000);
    } catch (err) {
      setEmailError(err.message);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleCancelEmailChange = () => {
    setIsChangingEmail(false);
    setEmailData({
      newEmail: '',
      currentPassword: ''
    });
    setEmailError('');
    setEmailSuccess('');
  };

  // Handler for notification preference toggle
  const handlePrefToggle = async (prefKey) => {
    const newValue = !notificationPrefs[prefKey];
    setPrefsSaving(prev => ({ ...prev, [prefKey]: true }));
    setPrefsError('');

    try {
      const response = await fetch(`${API_URL}/users/notification-preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ [prefKey]: newValue })
      });

      if (!response.ok) {
        throw new Error('Impossibile aggiornare le preferenze');
      }

      setNotificationPrefs(prev => ({ ...prev, [prefKey]: newValue }));
      // Show checkmark briefly
      setPrefsSaved(prev => ({ ...prev, [prefKey]: true }));
      setTimeout(() => setPrefsSaved(prev => ({ ...prev, [prefKey]: false })), 1500);
    } catch (err) {
      setPrefsError(err.message);
      // Revert on error
      setNotificationPrefs(prev => ({ ...prev, [prefKey]: !newValue }));
    } finally {
      setPrefsSaving(prev => ({ ...prev, [prefKey]: false }));
    }
  };

  // GDPR: Export user data
  const handleExportData = async () => {
    setIsExportingData(true);
    setExportError('');

    try {
      const response = await fetch(`${API_URL}/users/me/data`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Impossibile esportare i dati');
      }

      const data = await response.json();

      // Create and download JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `i-miei-dati-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err.message);
    } finally {
      setIsExportingData(false);
    }
  };

  // GDPR: Delete account
  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeleteError('Inserisci la password per confermare');
      return;
    }

    setIsDeleting(true);
    setDeleteError('');

    try {
      const response = await fetch(`${API_URL}/users/me`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ current_password: deletePassword })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Impossibile eliminare l\'account');
      }

      // Logout and redirect to home
      logout();
      navigate('/');
    } catch (err) {
      setDeleteError(err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeletePassword('');
    setDeleteError('');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-lg font-semibold text-gray-800">Il Mio Account</h1>
            {!isEditing && (
              <button
                onClick={() => {
                  setIsEditing(true);
                  // Close password and email change forms if open
                  if (isChangingPassword) {
                    handleCancelPasswordChange();
                  }
                  if (isChangingEmail) {
                    handleCancelEmailChange();
                  }
                }}
                disabled={isChangingPassword || isChangingEmail}
                className={`px-3 py-1.5 rounded text-xs font-medium transition ${
                  (isChangingPassword || isChangingEmail)
                    ? 'bg-blue-300 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                ✏️ Modifica
              </button>
            )}
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm mb-4">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm mb-4">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Personal Information Section */}
            <div>
              <h2 className="text-base font-semibold text-gray-800 mb-3">Informazioni Personali</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Nome
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                      required
                    />
                  ) : (
                    <p className="px-3 py-2 text-sm bg-gray-50 rounded text-gray-800">{formData.nome || 'N/D'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Cognome
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="cognome"
                      value={formData.cognome}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                      required
                    />
                  ) : (
                    <p className="px-3 py-2 text-sm bg-gray-50 rounded text-gray-800">{formData.cognome || 'N/D'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Numero di Telefono
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      name="numero_telefono"
                      value={formData.numero_telefono}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                    />
                  ) : (
                    <p className="px-3 py-2 text-sm bg-gray-50 rounded text-gray-800">{formData.numero_telefono || 'N/D'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Data di Nascita
                  </label>
                  {isEditing ? (
                    <input
                      type="date"
                      name="data_nascita"
                      value={formData.data_nascita}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                    />
                  ) : (
                    <p className="px-3 py-2 text-sm bg-gray-50 rounded text-gray-800">
                      {formData.data_nascita ? new Date(formData.data_nascita).toLocaleDateString('it-IT') : 'N/D'}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Account Information Section (Read-only for now) */}
            <div>
              <h2 className="text-base font-semibold text-gray-800 mb-3">Informazioni Account</h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <div className="px-3 py-2 bg-gray-50 rounded text-sm text-gray-800 flex justify-between items-center">
                    <span>{formData.email || 'N/D'}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setIsChangingEmail(true);
                        // Exit edit mode and close password form if active
                        if (isEditing) {
                          handleCancel();
                        }
                        if (isChangingPassword) {
                          handleCancelPasswordChange();
                        }
                      }}
                      disabled={isEditing || isChangingPassword}
                      className={`text-xs font-medium ${
                        (isEditing || isChangingPassword)
                          ? 'text-blue-300 cursor-not-allowed'
                          : 'text-blue-600 hover:text-blue-700'
                      }`}
                    >
                      Cambia Email
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <div className="px-3 py-2 bg-gray-50 rounded text-sm text-gray-800 flex justify-between items-center">
                    <span>••••••••</span>
                    <button
                      type="button"
                      onClick={() => {
                        setIsChangingPassword(true);
                        // Exit edit mode and close email form if active
                        if (isEditing) {
                          handleCancel();
                        }
                        if (isChangingEmail) {
                          handleCancelEmailChange();
                        }
                      }}
                      disabled={isEditing || isChangingEmail}
                      className={`text-xs font-medium ${
                        (isEditing || isChangingEmail)
                          ? 'text-blue-300 cursor-not-allowed'
                          : 'text-blue-600 hover:text-blue-700'
                      }`}
                    >
                      Cambia Password
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {isEditing && (
              <div className="flex gap-3 pt-4 border-t border-gray-100">
                <button
                  type="submit"
                  disabled={loading}
                  className={`flex-1 py-2.5 rounded text-sm font-medium transition ${
                    loading
                      ? 'bg-blue-300 text-white cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {loading ? 'Salvataggio...' : 'Salva Modifiche'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={loading}
                  className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded text-sm font-medium hover:bg-gray-200 transition disabled:bg-gray-100"
                >
                  Annulla
                </button>
              </div>
            )}
          </form>

          {/* Password Change Form - Separate from main form */}
          {isChangingPassword && (
            <div className="mt-4 border-t border-gray-100 pt-4">
              <h3 className="text-base font-semibold text-gray-800 mb-3">Cambia Password</h3>

              {passwordError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm mb-3">
                  {passwordError}
                </div>
              )}

              {passwordSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm mb-3">
                  {passwordSuccess}
                </div>
              )}

              <form onSubmit={handlePasswordSubmit} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Password Attuale
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.current ? "text" : "password"}
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-3 py-2 pr-9 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPasswords.current ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Nuova Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.new ? "text" : "password"}
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-3 py-2 pr-9 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPasswords.new ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Minimo 8 caratteri</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Conferma Nuova Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.confirm ? "text" : "password"}
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      className="w-full px-3 py-2 pr-9 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPasswords.confirm ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className={`flex-1 py-2.5 rounded text-sm font-medium transition ${
                      passwordLoading
                        ? 'bg-blue-300 text-white cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {passwordLoading ? 'Salvataggio...' : 'Salva Password'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelPasswordChange}
                    disabled={passwordLoading}
                    className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded text-sm font-medium hover:bg-gray-200 transition disabled:bg-gray-100"
                  >
                    Annulla
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Email Change Form - Separate from main form */}
          {isChangingEmail && (
            <div className="mt-4 border-t border-gray-100 pt-4">
              <h3 className="text-base font-semibold text-gray-800 mb-3">Cambia Email</h3>

              {emailError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm mb-3">
                  {emailError}
                </div>
              )}

              {emailSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm mb-3">
                  {emailSuccess}
                </div>
              )}

              <form onSubmit={handleEmailSubmit} className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Nuova Email
                  </label>
                  <input
                    type="email"
                    name="newEmail"
                    value={emailData.newEmail}
                    onChange={handleEmailChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                    required
                    placeholder="nuova.email@esempio.it"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Password Attuale (per conferma)
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.emailPassword ? "text" : "password"}
                      name="currentPassword"
                      value={emailData.currentPassword}
                      onChange={handleEmailChange}
                      className="w-full px-3 py-2 pr-9 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, emailPassword: !prev.emailPassword }))}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showPasswords.emailPassword ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Inserisci la tua password attuale per sicurezza</p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-xs text-blue-800">
                    <strong>Nota:</strong> Riceverai un'email di verifica al nuovo indirizzo. Clicca sul link nell'email per completare il cambio di email. Se non la ricevi, controlla la cartella spam/indesiderata.
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={emailLoading}
                    className={`flex-1 py-2.5 rounded text-sm font-medium transition ${
                      emailLoading
                        ? 'bg-blue-300 text-white cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {emailLoading ? 'Invio in corso...' : 'Invia Email di Verifica'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelEmailChange}
                    disabled={emailLoading}
                    className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded text-sm font-medium hover:bg-gray-200 transition disabled:bg-gray-100"
                  >
                    Annulla
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Notification Preferences Section */}
          <div className="mt-5 border-t border-gray-100 pt-4">
            <h2 className="text-base font-semibold text-gray-800 mb-3">
              Impostazioni Notifiche
            </h2>

            {prefsError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm mb-3">
                {prefsError}
              </div>
            )}

            {prefsLoading ? (
              <div className="text-xs text-gray-500">Caricamento preferenze...</div>
            ) : (
              <div className="space-y-2">
                {/* User Notifications */}
                <div className="px-3 py-2 bg-gray-50 rounded flex items-center justify-between">
                  <span className="text-xs text-gray-800">Mandami una mail quando le mie richieste vengono approvate o rifiutate</span>
                  <div className="flex items-center gap-2">
                    {prefsSaved.notify_participation_status && (
                      <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    <input
                      type="checkbox"
                      checked={notificationPrefs.notify_participation_status}
                      onChange={() => handlePrefToggle('notify_participation_status')}
                      disabled={prefsSaving.notify_participation_status}
                      className="w-4 h-4 rounded cursor-pointer"
                    />
                  </div>
                </div>

                <div className="px-3 py-2 bg-gray-50 rounded flex items-center justify-between">
                  <span className="text-xs text-gray-800">Mandami una mail quando una gita a cui sono iscritto viene annullata</span>
                  <div className="flex items-center gap-2">
                    {prefsSaved.notify_hike_cancelled && (
                      <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    <input
                      type="checkbox"
                      checked={notificationPrefs.notify_hike_cancelled}
                      onChange={() => handlePrefToggle('notify_hike_cancelled')}
                      disabled={prefsSaving.notify_hike_cancelled}
                      className="w-4 h-4 rounded cursor-pointer"
                    />
                  </div>
                </div>

                {/* Organizer Notifications - Only show if user is organizer */}
                {user?.is_organizer && (
                  <>
                    <h3 className="text-xs font-medium text-gray-700 mt-3">Per organizzatori</h3>
                    <div className="px-3 py-2 bg-gray-50 rounded flex items-center justify-between">
                      <span className="text-xs text-gray-800">Mandami una mail quando qualcuno si iscrive ad una mia gita</span>
                      <div className="flex items-center gap-2">
                        {prefsSaved.notify_new_registration && (
                          <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                        <input
                          type="checkbox"
                          checked={notificationPrefs.notify_new_registration}
                          onChange={() => handlePrefToggle('notify_new_registration')}
                          disabled={prefsSaving.notify_new_registration}
                          className="w-4 h-4 rounded cursor-pointer"
                        />
                      </div>
                    </div>

                    <div className="px-3 py-2 bg-gray-50 rounded flex items-center justify-between">
                      <span className="text-xs text-gray-800">Mandami una mail quando qualcuno si rimuove da una mia gita</span>
                      <div className="flex items-center gap-2">
                        {prefsSaved.notify_registration_cancelled && (
                          <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                        <input
                          type="checkbox"
                          checked={notificationPrefs.notify_registration_cancelled}
                          onChange={() => handlePrefToggle('notify_registration_cancelled')}
                          disabled={prefsSaving.notify_registration_cancelled}
                          className="w-4 h-4 rounded cursor-pointer"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Admin Notifications - Only show if user is admin */}
                {user?.is_admin && (
                  <>
                    <h3 className="text-xs font-medium text-gray-700 mt-3">Per amministratori</h3>
                    <div className="px-3 py-2 bg-gray-50 rounded flex items-center justify-between">
                      <span className="text-xs text-gray-800">Mandami una mail quando un nuovo utente si iscrive</span>
                      <div className="flex items-center gap-2">
                        {prefsSaved.notify_new_user_registered && (
                          <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                        <input
                          type="checkbox"
                          checked={notificationPrefs.notify_new_user_registered}
                          onChange={() => handlePrefToggle('notify_new_user_registered')}
                          disabled={prefsSaving.notify_new_user_registered}
                          className="w-4 h-4 rounded cursor-pointer"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* GDPR: Privacy & Data Section */}
          <div className="mt-5 border-t border-gray-100 pt-4">
            <h2 className="text-base font-semibold text-gray-800 mb-3">
              Privacy e Dati Personali
            </h2>
            <p className="text-xs text-gray-600 mb-3">
              In conformità al GDPR, puoi scaricare una copia dei tuoi dati o eliminare il tuo account.
              Per maggiori informazioni, consulta la nostra <Link to="/privacy" className="text-blue-600 hover:underline">Informativa sulla Privacy</Link>.
            </p>

            {exportError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm mb-3">
                {exportError}
              </div>
            )}

            <div className="space-y-3">
              {/* Export Data Button */}
              <div className="px-3 py-3 bg-gray-50 rounded">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800">Scarica i miei dati</p>
                    <p className="text-xs text-gray-500">Esporta tutti i tuoi dati personali in formato JSON</p>
                  </div>
                  <button
                    onClick={handleExportData}
                    disabled={isExportingData}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition ${
                      isExportingData
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {isExportingData ? 'Download...' : 'Scarica'}
                  </button>
                </div>
              </div>

              {/* Delete Account Section */}
              <div className="px-3 py-3 bg-red-50 rounded border border-red-100">
                {!showDeleteConfirm ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-red-800">Elimina account</p>
                      <p className="text-xs text-red-600">Questa azione è irreversibile e cancellerà tutti i tuoi dati</p>
                    </div>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 transition"
                    >
                      Elimina
                    </button>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm font-medium text-red-800 mb-2">Conferma eliminazione account</p>
                    <p className="text-xs text-red-600 mb-3">
                      Stai per eliminare definitivamente il tuo account. Verranno cancellati:
                    </p>
                    <ul className="text-xs text-red-600 mb-3 list-disc pl-4 space-y-1">
                      <li>Il tuo profilo e tutti i dati personali</li>
                      <li>Le tue iscrizioni alle gite</li>
                      <li>I tuoi preferiti</li>
                    </ul>

                    {deleteError && (
                      <div className="bg-red-100 border border-red-200 text-red-700 px-3 py-2 rounded text-xs mb-3">
                        {deleteError}
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="block text-xs font-medium text-red-700 mb-1">
                        Inserisci la tua password per confermare
                      </label>
                      <div className="relative">
                        <input
                          type={showDeletePassword ? "text" : "password"}
                          value={deletePassword}
                          onChange={(e) => setDeletePassword(e.target.value)}
                          className="w-full px-3 py-2 pr-9 text-sm border border-red-300 rounded focus:ring-1 focus:ring-red-500 focus:border-red-500"
                          placeholder="Password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowDeletePassword(!showDeletePassword)}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                          {showDeletePassword ? (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={handleDeleteAccount}
                        disabled={isDeleting}
                        className={`flex-1 py-2 rounded text-xs font-medium transition ${
                          isDeleting
                            ? 'bg-red-300 text-white cursor-not-allowed'
                            : 'bg-red-600 text-white hover:bg-red-700'
                        }`}
                      >
                        {isDeleting ? 'Eliminazione...' : 'Conferma eliminazione'}
                      </button>
                      <button
                        onClick={handleCancelDelete}
                        disabled={isDeleting}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded text-xs font-medium hover:bg-gray-300 transition"
                      >
                        Annulla
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}
